import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Crown, Power } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CatalogPage } from "@/components/common/CatalogPage";
import { plansApi } from "@/lib/api";
import type { SubscriptionPlan } from "@/lib/api/types";
import { fmtCurrency } from "@/lib/utils";

/** Subscription plans the app sells. Inactive plans are hidden from the app but kept for history. */
export default function PlansPage() {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (id: string) => plansApi.toggleActive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plans"] }),
  });

  return (
    <CatalogPage<SubscriptionPlan>
      queryKey="plans"
      title="Subscription plans"
      subtitle={(n) => `${n} plans. Only active plans are offered in the app.`}
      icon={Crown}
      noun="plan"
      searchPlaceholder="Search plans…"
      source={{ kind: "list", fetch: plansApi.all, searchText: (p) => `${p.name} ${p.displayName}` }}
      itemLabel={(p) => p.displayName}
      columns={[
        { header: "Plan", render: (p) => (
          <div>
            <p className="font-semibold">{p.displayName}</p>
            <p className="font-mono text-[11px] text-muted-foreground">{p.name}</p>
          </div>
        ) },
        { header: "Price", render: (p) => <span className="font-semibold">{fmtCurrency(p.price, p.currency ?? "TZS")}</span> },
        { header: "Duration", render: (p) => p.durationLabel || `${p.durationDays} days` },
        // The seeded plans use -1 for "unlimited".
        { header: "Consultations", render: (p) => (p.consultationsLimit == null || p.consultationsLimit < 0 ? "Unlimited" : p.consultationsLimit) },
        { header: "Status", render: (p) => (p.isActive === false ? <Badge variant="muted">Inactive</Badge> : <Badge variant="success">Active</Badge>) },
        { header: "Order", render: (p) => p.sortOrder ?? 0 },
      ]}
      rowActions={(p) => (
        <Button
          size="sm"
          variant="outline"
          title={p.isActive === false ? "Activate" : "Deactivate"}
          disabled={toggle.isPending}
          onClick={() => toggle.mutate(p.id)}
        >
          <Power className={`size-3.5 ${p.isActive === false ? "text-muted-foreground" : "text-success"}`} />
        </Button>
      )}
      fields={[
        { key: "name", label: "Code", required: true, half: true, placeholder: "GOLD" },
        { key: "displayName", label: "Display name", required: true, half: true, placeholder: "Gold" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "price", label: "Price", type: "number", required: true, half: true, placeholder: "15000" },
        { key: "currency", label: "Currency", half: true, placeholder: "TZS" },
        { key: "durationDays", label: "Duration (days)", type: "number", required: true, half: true, placeholder: "30" },
        { key: "durationLabel", label: "Duration label", half: true, placeholder: "Monthly" },
        { key: "features", label: "Features (one per line)", type: "textarea" },
        { key: "consultationsLimit", label: "Consultations limit (blank or -1 = unlimited)", type: "number", half: true },
        { key: "familyMembersLimit", label: "Family members limit", type: "number", half: true },
        { key: "healthTracking", label: "Health tracking", half: true, placeholder: "Basic / Advanced" },
        { key: "sortOrder", label: "Sort order", type: "number", half: true, placeholder: "0" },
        { key: "prioritySupport", label: "Priority support", type: "checkbox" },
        { key: "exclusiveContent", label: "Exclusive content", type: "checkbox" },
        { key: "isActive", label: "Active (offered in the app)", type: "checkbox", defaultChecked: true },
      ]}
      create={plansApi.create}
      update={plansApi.update}
      remove={plansApi.remove}
    />
  );
}
